import React from "react";
import { Check, Gift } from "lucide-react";
import { ALCANCE, PRECIOS, etiquetaPrecio, formatCLP } from "../../data/precios";
import { INCENTIVO, incentivoActivo } from "../../data/incentivo";
import { precioDesde } from "../../data/segmentos";
import WhatsAppCta from "./WhatsAppCta";

/**
 * Los packs de un servicio.
 *
 * CÓMO ESTÁN PENSADOS LOS TRES. El primero es la entrada: corto y con lo
 * justo, para que exista un precio bajo publicado pero casi nadie lo quiera.
 * El segundo es el que se quiere vender: va al centro, en oscuro y con la
 * etiqueta, y da bastante más que el primero por la diferencia de precio. El
 * tercero es el premium y contrasta fuerte con el segundo: quien quiere "todo"
 * tiene dónde ir, y de paso hace que el del medio se vea razonable.
 *
 * El error que esto evita es meter todo en el pack uno. Si el más barato ya
 * entrega lo que el cliente quiere, nadie mira los otros dos.
 *
 * `id` y `titulo` existen porque /precios pinta este bloque una vez por
 * servicio en la misma página: cada uno necesita su ancla y su encabezado.
 *
 * `alcance` se apaga justo ahí: la condición de zona y traslado ya va en la
 * entrada de /precios, y repetirla en cada bloque de servicio la convierte en
 * ruido que se deja de leer. En una página de servicio, en cambio, este es el
 * único lugar donde el cliente ve un número.
 */
const FotoPacks = ({
  segmento,
  id = "packs",
  titulo,
  alcance = true,
  className = "bg-sand-50 py-20 md:py-28",
}) => {
  if (!segmento?.packs?.length) return null;

  const varios = segmento.packs.length > 1;
  const hayIncentivo = incentivoActivo();

  return (
    <section id={id} className={className}>
      <div className="container-page">
        <h2 className="section-title max-w-2xl">
          {titulo ||
            (varios
              ? "Elige cuánto quieres guardar."
              : `Un solo precio: ${formatCLP(precioDesde(segmento))} por ${segmento.unidad}.`)}
        </h2>
        <p className="section-copy mt-5 max-w-2xl">
          {varios
            ? `${segmento.packs.length === 2 ? "Dos" : "Tres"} packs con precio cerrado, desde ${formatCLP(
                precioDesde(segmento)
              )}. Lo que ves es lo que se cobra. Factura.`
            : `Lo que ves es lo que se cobra, con precio cerrado. ${segmento.entrega} de entrega. Factura.`}
        </p>
        {alcance ? (
          <p className="mt-3 max-w-2xl text-sm text-navy-500">{ALCANCE.linea}</p>
        ) : null}

        {hayIncentivo ? (
          <p className="mt-8 flex items-start gap-3 rounded-2xl border border-sand-300 bg-sand-100 px-5 py-4 text-navy-900">
            <Gift className="mt-0.5 h-5 w-5 shrink-0 text-sand-600" aria-hidden="true" />
            <span>
              <span className="font-semibold">{INCENTIVO.frase()}</span>{" "}
              <span className="text-sm text-navy-600">{INCENTIVO.condicion}</span>
            </span>
          </p>
        ) : null}

        <div
          className={`mt-10 grid items-stretch gap-6 ${
            !varios ? "lg:max-w-xl" : segmento.packs.length === 2 ? "lg:max-w-4xl lg:grid-cols-2" : "lg:grid-cols-3"
          }`}
        >
          {segmento.packs.map((pack) => (
            <article
              key={pack.id}
              className={`relative flex h-full flex-col rounded-3xl p-8 ${
                pack.destacado
                  ? "bg-navy-900 text-white shadow-lift lg:-my-4 lg:py-12"
                  : "border border-navy-100 bg-white text-navy-900 shadow-card"
              }`}
            >
              {pack.badge ? (
                <span
                  className={`mb-5 inline-flex w-fit rounded-full px-3 py-1.5 text-xs font-semibold ${
                    pack.destacado ? "bg-sand-400 text-navy-900" : "bg-sand-100 text-navy-800"
                  }`}
                >
                  {pack.badge}
                </span>
              ) : (
                <span className="mb-5 hidden h-[1.9rem] lg:block" aria-hidden="true" />
              )}

              <h3 className="font-display text-2xl font-bold tracking-tight">{pack.nombre}</h3>
              <p className={`mt-1 text-sm ${pack.destacado ? "text-navy-200" : "text-navy-500"}`}>
                {pack.volumen}
                {pack.precioNota ? ` · ${pack.precioNota}` : ""}
              </p>

              <p className="mt-6 font-display text-4xl font-extrabold tracking-tight tabular-nums md:text-5xl">
                {etiquetaPrecio(PRECIOS[pack.priceKey], { ivaIncluido: pack.ivaIncluido })}
                {varios ? null : (
                  <span className="ml-2 text-lg font-bold text-sand-300">/ {segmento.unidad}</span>
                )}
              </p>

              <p className={`mt-7 text-sm font-semibold ${pack.destacado ? "text-sand-200" : "text-navy-700"}`}>
                Incluye
              </p>
              <ul className="mt-4 flex-1 space-y-3">
                {pack.incluye.map((item) => (
                  <li key={item} className="flex items-start gap-2.5 text-sm leading-relaxed">
                    <Check
                      className={`mt-0.5 h-4 w-4 shrink-0 ${pack.destacado ? "text-sand-300" : "text-navy-700"}`}
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <WhatsAppCta
                message={
                  varios ? `${segmento.whatsapp} Me interesa el pack ${pack.nombre}.` : segmento.whatsapp
                }
                pack={pack.nombre}
                location={`packs_${segmento.id}_${pack.id}`}
                variant={pack.destacado ? "sand" : "dark"}
                className="mt-8 w-full"
              >
                {varios ? `Reservar ${pack.nombre}` : "Pedir fecha"}
              </WhatsAppCta>
            </article>
          ))}
          {segmento.extras?.length ? (
            <ul className="mt-2 divide-y divide-navy-100 rounded-2xl border border-navy-100 bg-white lg:max-w-xl">
              {segmento.extras.map((extra) => (
                <li key={extra.id} className="flex items-baseline justify-between gap-4 px-6 py-4">
                  <span className="text-navy-900">
                    {extra.nombre}
                    {extra.nota ? (
                      <span className="mt-0.5 block text-sm text-navy-500">{extra.nota}</span>
                    ) : null}
                  </span>
                  <span className="shrink-0 font-display text-lg font-bold tabular-nums text-navy-900">
                    + {etiquetaPrecio(PRECIOS[extra.priceKey], { ivaIncluido: extra.ivaIncluido })}
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </div>
    </section>
  );
};

export default FotoPacks;
